/**
 * HelcimPlanDetailModal
 * Full-screen modal matching the Helcim plan detail view:
 *   Overview | Upcoming Billing | Add-ons
 * Plus ... menu → Edit / Delete / Deactivate
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  IonModal,
  IonButton,
  IonIcon,
  IonContent,
  IonGrid,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonList,
  IonItem,
  IonText,
  IonBadge,
  IonSpinner,
  IonInput,
  IonCard,
} from '@ionic/react';
import {
  closeOutline,
  ellipsisVertical,
  createOutline,
  pencilOutline,
  trashOutline,
  pauseCircleOutline,
  playCircleOutline,
  calendarOutline,
  cashOutline,
  extensionPuzzleOutline,
  timeOutline,
  peopleOutline,
  repeatOutline,
  linkOutline,
  personAddOutline,
} from 'ionicons/icons';
import type { HelcimPaymentPlan, HelcimSubscription } from '@rsmaids/shared/services/api/helcim/helcim';
import {
  getSubscriptions,
  updatePaymentPlan,
  deletePaymentPlan,
  getAllHelcimCustomers,
} from '../services/helcimInvoicingService';
import styles from './HelcimPlanDetailModal.module.scss';
import BasePageWithHeader from './BasePageWithHeader';
import GlassConfirmSheet from './GlassConfirmSheet';
import HelcimSubscriptionsModal from './HelcimSubscriptionsModal';

import AddSubscriberModal from './AddSubscriberModal';
import { useTranslation } from 'react-i18next';
import { translateDynamic } from '../i18n/dynamic';
import { intlLocale } from '../i18n/locale';

interface Props {
  plan: HelcimPaymentPlan | null;
  isOpen: boolean;
  allPlans?: HelcimPaymentPlan[];
  merchantTaxRate?: number | null;
  onClose: () => void;
  onPlanChanged: () => void;
  onPlanUpdated?: (updated: HelcimPaymentPlan) => void; // silent in-place update
  onEdit: () => void; // opens the edit form sheet in parent
  onNavigateToContact?: (contactId: string) => void;
}

interface OverflowMenuOption {
  value: string;
  label: string;
  icon: string;
  destructive?: boolean;
}

// Same anchored-portal mechanism as HelcimSubscriptionDetailModal.tsx's
// OverflowMenu — portals an IonCard into an in-page portalRef container
// instead of relying on CardActionsPopover's IonPopover.
type OverflowMenuRect = { top: number; right: number };

function useOverflowMenuPosition(
  isOpen: boolean,
  anchorRef: React.RefObject<HTMLElement | null>,
  portalRef: React.RefObject<HTMLElement | null>,
  cardRef: React.RefObject<HTMLElement | null>,
) {
  const [rect, setRect] = useState<OverflowMenuRect | null>(null);
  // Clear the computed rect the moment the menu closes — computed during
  // render rather than as a synchronous setState call at the top of the
  // effect below.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen && rect !== null) setRect(null);
  }

  useEffect(() => {
    if (!isOpen) return;
    const computeRect = (): OverflowMenuRect | null => {
      const field = anchorRef.current;
      const portal = portalRef.current;
      if (!field || !portal) return null;
      const box = field.getBoundingClientRect();
      const anchor = portal.getBoundingClientRect();
      return { top: box.bottom - anchor.top + 4, right: anchor.right - box.right };
    };
    const applyRect = (r: OverflowMenuRect) => {
      const card = cardRef.current as HTMLElement | null;
      if (!card) return;
      card.style.top = `${r.top}px`;
      card.style.right = `${r.right}px`;
    };

    const initial = computeRect();
    setRect(initial);

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const r = computeRect();
        if (r) { setRect(r); applyRect(r); }
      });
    });

    const handleScroll = () => { const r = computeRect(); if (r) applyRect(r); };
    const handleResize = () => { const r = computeRect(); if (r) { setRect(r); applyRect(r); } };
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, anchorRef, portalRef, cardRef]);

  return rect;
}

const OverflowMenu: React.FC<{
  isOpen: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
  portalRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  options: OverflowMenuOption[];
  onSelect: (value: string) => void;
}> = ({ isOpen, anchorRef, portalRef, onClose, options, onSelect }) => {
  const cardRef = useRef<HTMLIonCardElement>(null);
  const rect = useOverflowMenuPosition(isOpen, anchorRef, portalRef, cardRef);
  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(portalRef.current);
  }, [portalRef, isOpen]);

  if (!isOpen || !rect || !portalNode) return null;

  return createPortal(
    <>
      <div className={styles.dropdownBackdrop} onClick={onClose} />
      <IonCard ref={cardRef} className={styles.customDropdownList} style={{ ...rect, position: 'absolute' }}>
        <IonList lines="none" className={styles.customDropdownListInner}>
          {options.map(opt => (
            <IonItem
              key={opt.value}
              button
              lines="none"
              detail={false}
              className={`${styles.customDropdownItem} ${opt.destructive ? styles.customDropdownItemDestructive : ''}`}
              onClick={() => onSelect(opt.value)}
            >
              <IonIcon icon={opt.icon} slot="start" className={styles.customDropdownItemIcon} />
              <IonLabel>{opt.label}</IonLabel>
            </IonItem>
          ))}
        </IonList>
      </IonCard>
    </>,
    portalNode,
  );
};

const HelcimPlanDetailModal: React.FC<Props> = ({ plan, isOpen, allPlans = [], merchantTaxRate = null, onClose, onPlanChanged, onPlanUpdated, onEdit, onNavigateToContact }) => {
  const { t } = useTranslation();
  const [tab, setTab]                     = useState<'overview' | 'billing' | 'addons'>('overview');
  const [subscriptions, setSubscriptions] = useState<HelcimSubscription[]>([]);
  const [loadingSubs, setLoadingSubs]     = useState(false);
  const [customerCache, setCustomerCache] = useState<Record<string, string>>({});
  const [deleteOpen, setDeleteOpen]       = useState(false);
  const [subsModalOpen, setSubsModalOpen] = useState(false);
  const [addSubscriberOpen, setAddSubscriberOpen] = useState(false);
  const [editing, setEditing]         = useState<'name' | 'desc' | null>(null);
  const [formName, setFormName]       = useState('');
  const [formDesc, setFormDesc]       = useState('');
  const [formSaving, setFormSaving]   = useState(false);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const overflowTriggerRef = useRef<HTMLIonButtonElement>(null);
  const overflowPortalRef = useRef<HTMLDivElement>(null);

  // ── Load subscriptions for this plan ──────────────────────────────────────
  const loadSubs = useCallback(async () => {
    if (!plan?.id) return;
    setLoadingSubs(true);
    try {
      const [subs, customers] = await Promise.all([
        getSubscriptions({ paymentPlanId: plan.id, skipEnrichment: true }),
        getAllHelcimCustomers().catch(() => []),
      ]);
      const cc: Record<string, string> = {};
      customers.forEach(c => {
        if (c.customerCode) {
          cc[c.customerCode] = (c.contactName?.trim()) || (c.businessName?.trim()) || c.customerCode;
        }
      });
      setCustomerCache(cc);
      setSubscriptions(subs.subscriptions);
    } catch {
      setSubscriptions([]);
    } finally {
      setLoadingSubs(false);
    }
  }, [plan]);

  // Reset the tab/subscriptions view whenever the modal opens on a
  // (possibly different) plan — computed during render (React's "adjust
  // state when a dependency changes" pattern) rather than as synchronous
  // setState calls inside an effect.
  const [prevTabResetKey, setPrevTabResetKey] = useState<{ isOpen: boolean; planId: unknown }>({ isOpen, planId: plan?.id });
  if (prevTabResetKey.isOpen !== isOpen || prevTabResetKey.planId !== plan?.id) {
    setPrevTabResetKey({ isOpen, planId: plan?.id });
    if (isOpen && plan?.id) {
      setTab('overview');
      setSubscriptions([]);
      setShowOverflowMenu(false);
    }
  }

  useEffect(() => {
    if (tab === 'billing' && plan?.id && subscriptions.length === 0) {
      // Deferred via a resolved-promise microtask (not called synchronously
      // in the effect body) — loadSubs's own setLoadingSubs(true) (inside
      // its try/catch) is needed on every real invocation, so it can't just
      // be dropped the way a mount-only redundant reset could be.
      Promise.resolve().then(() => loadSubs());
    }
  }, [tab, plan?.id, subscriptions.length, loadSubs]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const fmt = (val?: number, currency?: string) =>
    val != null
      ? new Intl.NumberFormat('en-US', { style: 'currency', currency: currency ?? 'USD' }).format(val)
      : '—';

  const periodLabel = () => {
    const period = (plan?.billingPeriod ?? '').toLowerCase();
    const inc    = plan?.billingPeriodIncrements ?? 1;
    const map: Record<string, string> = { daily: 'Day', weekly: 'Week', monthly: 'Month', yearly: 'Year' };
    const p = map[period] ?? period;
    return inc > 1 ? t('components.HelcimPlanDetailModal.everyS', { inc, p: translateDynamic(p + 's') }) : t('components.HelcimPlanDetailModal.every', { p: translateDynamic(p) });
  };

  const billingDay = () => {
    const d = plan?.dateBilling as string | undefined;
    if (!d || d === 'Sign-up') return 'On Sign-up';
    return d;
  };

  const isActive = (plan?.status ?? '').toLowerCase() === 'active';

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleToggleActive = () => {
    if (!plan?.id) return;
    const newStatus = isActive ? 'inactive' : 'active';
    // Optimistic update — notify parent to flip status immediately
    if (onPlanUpdated && plan) onPlanUpdated({ ...plan, status: newStatus });
    updatePaymentPlan(plan.id, { status: newStatus } as Record<string, unknown>)
      .then(() => onPlanChanged()) // silent background sync
      .catch(() => {
        // Revert on error
        if (onPlanUpdated && plan) onPlanUpdated({ ...plan, status: plan.status });
      });
  };

  const handleDelete = async () => {
    if (!plan?.id) return;
    const id = plan.id;
    // Close immediately — feels instant
    onClose();
    onPlanChanged();
    // Fire delete in background
    deletePaymentPlan(id).catch(() => {
      // silent — parent will re-sync on next load
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (!plan) return null;

  return (
    <>
    <IonModal isOpen={isOpen} onDidDismiss={onClose} className={styles.modal}>
      <div ref={overflowPortalRef} className={styles.pickerPortalRoot} />
      <OverflowMenu
        isOpen={showOverflowMenu}
        anchorRef={overflowTriggerRef}
        portalRef={overflowPortalRef}
        onClose={() => setShowOverflowMenu(false)}
        onSelect={(action) => {
          setShowOverflowMenu(false);
          if (action === 'edit') onEdit();
          else if (action === 'toggle') handleToggleActive();
          else if (action === 'delete') setDeleteOpen(true);
        }}
        options={[
          { value: 'edit', label: t('components.HelcimPlanDetailModal.editPlan'), icon: createOutline },
          { value: 'toggle', label: isActive ? t('components.HelcimPlanDetailModal.deactivatePlan') : t('components.HelcimPlanDetailModal.activatePlan'), icon: isActive ? pauseCircleOutline : playCircleOutline },
          { value: 'delete', label: t('components.HelcimPlanDetailModal.deletePlan'), icon: trashOutline, destructive: true },
        ]}
      />
      <BasePageWithHeader
        toolbarClassName={styles.toolbar}
        title={plan.name ?? t('components.HelcimPlanDetailModal.plan', { id: plan.id })}
        titleNode={
          <div className={styles.titleArea}>
            <IonBadge className={`${styles.statusBadge} ${isActive ? styles.badgeSuccess : styles.badgeMedium}`}>
              {translateDynamic(plan.status ?? 'unknown')}
            </IonBadge>
          </div>
        }
        startButtons={
          <IonButton fill="clear" className={styles.closeBtn} onClick={onClose}>
            <IonIcon icon={closeOutline} />
          </IonButton>
        }
        endButtons={
          <IonButton
            fill="clear"
            className={styles.menuBtn}
            onClick={(e) => {
              overflowTriggerRef.current = e.currentTarget;
              setShowOverflowMenu(o => !o);
            }}
          >
            <IonIcon icon={ellipsisVertical} />
          </IonButton>
        }
      >
        <IonSegment
          value={tab}
          onIonChange={e => setTab(e.detail.value as typeof tab)}
          className={styles.segment}
          scrollable
        >
          <IonSegmentButton value="overview"><IonLabel>{t('components.HelcimPlanDetailModal.overview')}</IonLabel></IonSegmentButton>
          <IonSegmentButton value="billing"><IonLabel>{t('components.HelcimPlanDetailModal.upcomingBilling')}</IonLabel></IonSegmentButton>
          <IonSegmentButton value="addons"><IonLabel>{t('components.HelcimPlanDetailModal.addOns')}</IonLabel></IonSegmentButton>
        </IonSegment>

      <IonContent className={styles.content}>
        <IonGrid className={styles.scrollContainer}>

        {/* ── Overview tab ── */}
        {tab === 'overview' && (
          <>
            {/* Hero pricing card */}
            <div className={styles.heroCard}>
              <span className={styles.heroPeriod}>{periodLabel().toUpperCase()}</span>
              <span className={styles.heroAmount}>{fmt(plan.recurringAmount, plan.currency)}</span>
              <span className={styles.heroCurrency}>{(plan.currency ?? 'USD').toUpperCase()}</span>
            </div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>{t('components.HelcimPlanDetailModal.planInformation')}</h3>
              <IonList className={styles.infoList}>
                <IonItem className={styles.infoItem} lines="none">
                  <IonLabel>{t('components.HelcimPlanDetailModal.planName')}</IonLabel>
                  {editing === 'name'
                    ? <IonInput value={formName} onIonInput={e => setFormName(String(e.detail.value ?? ''))} placeholder={t('components.HelcimPlanDetailModal.planName2')} autoFocus className={styles.inlineInput} />
                    : <IonText slot="end" className={styles.infoValue}>{plan.name ?? t('components.HelcimPlanDetailModal.plan', { id: plan.id })}</IonText>
                  }
                  <IonButton
                    slot="end" fill="clear"
                    disabled={formSaving}
                    className={styles.inlineIconBtn}
                    onClick={async () => {
                      if (editing === 'name') {
                        if (!plan?.id || !formName.trim()) return;
                        setFormSaving(true);
                        try {
                          await updatePaymentPlan(plan.id, { name: formName.trim(), description: plan.description ?? '', type: 'subscription', billingPeriod: plan.billingPeriod ?? 'monthly', billingPeriodIncrements: plan.billingPeriodIncrements ?? 1, recurringAmount: plan.recurringAmount ?? 0, currency: (plan.currency ?? 'USD') as 'USD' | 'CAD' });
                          setEditing(null); onPlanChanged();
                        } catch (e) { alert(e instanceof Error ? e.message : String(e)); } finally { setFormSaving(false); }
                      } else {
                        setFormName(plan?.name ?? ''); setEditing('name');
                      }
                    }}
                  >
                    {formSaving && editing === 'name' ? <IonSpinner name="dots" /> : <IonIcon icon={pencilOutline} color={editing === 'name' ? 'primary' : 'medium'} />}
                  </IonButton>
                </IonItem>
                <IonItem className={styles.infoItem} lines="full">
                  <IonLabel>{t('components.HelcimPlanDetailModal.description')}</IonLabel>
                  {editing === 'desc'
                    ? <IonInput value={formDesc} onIonInput={e => setFormDesc(String(e.detail.value ?? ''))} placeholder={t('components.HelcimPlanDetailModal.description')} autoFocus className={styles.inlineInput} />
                    : plan?.description
                      ? <IonText slot="end" className={styles.infoValue}>{plan.description as string}</IonText>
                      : null
                  }
                  <IonButton
                    slot="end" fill="clear"
                    disabled={formSaving}
                    className={styles.inlineIconBtn}
                    onClick={async () => {
                      if (editing === 'desc') {
                        if (!plan?.id) return;
                        setFormSaving(true);
                        try {
                          await updatePaymentPlan(plan.id, { name: plan.name ?? '', description: formDesc.trim(), type: 'subscription', billingPeriod: plan.billingPeriod ?? 'monthly', billingPeriodIncrements: plan.billingPeriodIncrements ?? 1, recurringAmount: plan.recurringAmount ?? 0, currency: (plan.currency ?? 'USD') as 'USD' | 'CAD' });
                          setEditing(null); onPlanChanged();
                        } catch (e) { alert(e instanceof Error ? e.message : String(e)); } finally { setFormSaving(false); }
                      } else {
                        setFormDesc((plan?.description as string | undefined) ?? ''); setEditing('desc');
                      }
                    }}
                  >
                    {formSaving && editing === 'desc' ? <IonSpinner name="dots" /> : <IonIcon icon={pencilOutline} color={editing === 'desc' ? 'primary' : 'medium'} />}
                  </IonButton>
                </IonItem>
                <IonItem className={styles.infoItem} lines="none">
                  <IonIcon icon={cashOutline} slot="start" className={styles.infoIcon} />
                  <IonLabel>{t('components.HelcimPlanDetailModal.setUpFee')}</IonLabel>
                  <IonText slot="end" className={styles.infoValue}>
                    {plan.setupAmount && plan.setupAmount > 0 ? fmt(plan.setupAmount, plan.currency) : t('components.HelcimPlanDetailModal.no')}
                  </IonText>
                </IonItem>
                <IonItem className={styles.infoItem} lines="none">
                  <IonIcon icon={timeOutline} slot="start" className={styles.infoIcon} />
                  <IonLabel>{t('components.HelcimPlanDetailModal.trialPeriod')}</IonLabel>
                  <IonText slot="end" className={styles.infoValue}>
                    {(() => {
                      const days = Number((plan as Record<string, unknown>).freeTrialPeriod ?? 0);
                      if (!days) return 'No';
                      if (days % 7 === 0) return `${days / 7} week${days / 7 !== 1 ? 's' : ''}`;
                      return `${days} day${days !== 1 ? 's' : ''}`;
                    })()}
                  </IonText>
                </IonItem>
                <IonItem className={styles.infoItem} lines="none">
                  <IonIcon icon={calendarOutline} slot="start" className={styles.infoIcon} />
                  <IonLabel>{t('components.HelcimPlanDetailModal.billingDay')}</IonLabel>
                  <IonText slot="end" className={styles.infoValue}>{billingDay()}</IonText>
                </IonItem>
                <IonItem className={styles.infoItem} lines="none">
                  <IonIcon icon={repeatOutline} slot="start" className={styles.infoIcon} />
                  <IonLabel>{t('components.HelcimPlanDetailModal.termLength')}</IonLabel>
                  <IonText slot="end" className={styles.infoValue}>{t('components.HelcimPlanDetailModal.forever')}</IonText>
                </IonItem>
                <IonItem className={styles.infoItem} lines="none">
                  <IonIcon icon={calendarOutline} slot="start" className={styles.infoIcon} />
                  <IonLabel>{t('components.HelcimPlanDetailModal.dateCreated')}</IonLabel>
                  <IonText slot="end" className={styles.infoValue}>
                    {plan.dateCreated
                      ? new Date(plan.dateCreated).toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric', year: 'numeric' })
                      : '—'}
                  </IonText>
                </IonItem>
                <IonItem className={styles.infoItem} lines="none" button detail onClick={() => setSubsModalOpen(true)}>
                  <IonIcon icon={peopleOutline} slot="start" className={styles.infoIcon} />
                  <IonLabel>{t('components.HelcimPlanDetailModal.subscribers')}</IonLabel>
                  <IonText slot="end" className={`${styles.infoValue} ${styles.infoValueHighlight}`}>
                    {plan.subscriberCount ?? 0}
                  </IonText>
                </IonItem>
              </IonList>
            </div>

            {/* Action rows */}
            <div className={styles.section}>
              <IonList className={styles.actionList}>
                <IonItem className={styles.actionItem} lines='none' button detail onClick={() => setAddSubscriberOpen(true)}>
                  <IonIcon icon={personAddOutline} slot="start" className={styles.actionIcon} />
                  <IonLabel>
                    <h2>{t('components.HelcimPlanDetailModal.addSubscribers')}</h2>
                    <p>{t('components.HelcimPlanDetailModal.addCustomersToThePlan')}</p>
                  </IonLabel>
                </IonItem>

              </IonList>
            </div>
          </>
        )}

        {/* ── Upcoming Billing tab ── */}
        {tab === 'billing' && (
          <div className={styles.section}>
            {loadingSubs ? (
              <div className={styles.center}><IonSpinner name="bubbles" color="primary" /></div>
            ) : subscriptions.length === 0 ? (
              <div className={styles.center}>
                <IonText color="medium"><p>{t('components.HelcimPlanDetailModal.noUpcomingBillingFound')}</p></IonText>
              </div>
            ) : (
              <>
                <div className={styles.billingHeader}>
                  <span className={styles.billingHeaderCustomer}>{t('components.HelcimPlanDetailModal.customer')}</span>
                  <span className={styles.billingHeaderAmount}>{t('components.HelcimPlanDetailModal.amount')}</span>
                  <span className={styles.billingHeaderDate}>{t('components.HelcimPlanDetailModal.paymentDate')}</span>
                </div>
                <div className={styles.billingList}>
                  {subscriptions.map(sub => {
                    const raw = sub as Record<string, unknown>;
                    const name = (raw['customerName'] as string | null)
                      ?? (sub.customerCode ? customerCache[sub.customerCode] : null)
                      ?? sub.customerCode
                      ?? `Subscription #${sub.id}`;
                    const subLabel = t('components.HelcimPlanDetailModal.id', { id: sub.id });
                    const taxAmt  = (raw['taxAmount']   as number | undefined) ?? 0;
                    const baseAmt = sub.recurringAmount ?? 0;
                    const amount  = (raw['totalAmount'] as number | undefined) ?? (taxAmt > 0 ? baseAmt + taxAmt : baseAmt);
                    const dateStr = sub.dateBilling
                      ? new Date(sub.dateBilling + 'T00:00:00').toLocaleDateString(intlLocale(), { month: 'numeric', day: 'numeric', year: 'numeric' })
                      : '—';
                    return (
                    <div key={sub.id} className={styles.billingRow}>
                      <div className={styles.billingColCustomer}>
                        <div className={styles.billingCustomerInner}>
                          <div className={styles.billingName}>{name}</div>
                          <div className={styles.billingSubLabel}>{subLabel}</div>
                        </div>
                      </div>
                      <div className={styles.billingColAmount}>
                        <span className={styles.billingAmount}>{fmt(amount, plan.currency ?? 'USD')}</span>
                        <span className={styles.billingTax}>{fmt(baseAmt, plan.currency ?? 'USD')}{t('components.HelcimPlanDetailModal.tax')}</span>
                      </div>
                      <div className={styles.billingColDate}>
                        <span className={styles.billingDateText}>{dateStr}</span>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Add-ons tab ── */}
        {tab === 'addons' && (
          <div className={styles.addonsTab}>
            <div className={styles.addonsCard}>
              <div className={styles.addonsCardLeft}>
                <div className={styles.addonsIconWrap}>
                  <IonIcon icon={linkOutline} className={styles.addonsIcon} />
                </div>
                <div>
                  <p className={styles.addonsCardTitle}>{t('components.HelcimPlanDetailModal.linkedAddOns')}</p>
                  <p className={styles.addonsCardSub}>{t('components.HelcimPlanDetailModal.addOnsLinkedToThisPlan')}</p>
                </div>
              </div>
              <IonButton fill="outline" size="small" className={styles.manageBtn}>{t('components.HelcimPlanDetailModal.linkAddOns')}
              </IonButton>
            </div>
            <div className={styles.emptyState}>
              <IonIcon icon={extensionPuzzleOutline} className={styles.emptyIcon} />
              <p className={styles.emptyTitle}>{t('components.HelcimPlanDetailModal.noAddOnsLinked')}</p>
              <p className={styles.emptySub}>{t('components.HelcimPlanDetailModal.startLinkingYourFirstAddOn')}</p>
            </div>
          </div>
        )}

        </IonGrid>
      </IonContent>

      {/* ── Delete confirmation ── */}
      <GlassConfirmSheet
        isOpen={deleteOpen}
        title={t('components.HelcimPlanDetailModal.deletePlan')}
        message={t('components.HelcimPlanDetailModal.permanentlyDeleteThisCannotBeUndone', { v1: plan.name ?? 'this plan' })}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onDismiss={() => setDeleteOpen(false)}
      />

    </BasePageWithHeader>
    </IonModal>

    <HelcimSubscriptionsModal
      isOpen={subsModalOpen}
      onClose={() => setSubsModalOpen(false)}
      paymentPlanId={plan?.id}
      planName={plan?.name ?? undefined}
      title={t('components.HelcimPlanDetailModal.subscribers')}
      onNavigateToContact={onNavigateToContact ? (contactId) => { setSubsModalOpen(false); onNavigateToContact(contactId); } : undefined}
      onSubscriberAdded={() => {
        if (onPlanUpdated && plan) {
          onPlanUpdated({ ...plan, subscriberCount: (plan.subscriberCount ?? 0) + 1 });
        }
        onPlanChanged();
      }}
    />
    <AddSubscriberModal
      isOpen={addSubscriberOpen}
      plan={plan}
      allPlans={allPlans.length > 0 ? allPlans : undefined}
      merchantTaxRate={merchantTaxRate ?? null}
      onClose={() => setAddSubscriberOpen(false)}
      onSubscriberAdded={() => {
        setAddSubscriberOpen(false);
        if (onPlanUpdated && plan) {
          onPlanUpdated({ ...plan, subscriberCount: (plan.subscriberCount ?? 0) + 1 });
        }
        onPlanChanged();
      }}
    />
    </>
  );
};

export default HelcimPlanDetailModal;
